import { create } from 'zustand';
import { marcarTodasNotificacionesLeidasWeb } from '../lib/notificacionesApi';
import { supabase } from '../lib/supabase';
import { createUnpaidDeliveredDigestAlert } from '../lib/alerts/alertGenerator';
import { collapseInventarioBajoAlerts } from '../lib/alerts/collapseInventarioBajoAlerts';

export interface Alert {
  id: string;
  empresa_id: string;
  type: 'nuevo_lead' | 'nuevo_pedido' | 'venta_en_linea' | 'nueva_cita' | 'compra_realizada' | 'factura_vencida' | 'factura_no_pagada' | 'entrega_tarde' | 'cita_proxima' | 'cita_pendiente_confirmar' | 'feriado_proximo' | 'cliente_mes' | 'producto_mas_vendido' | 'inventario_bajo' | 'lead_sin_contactar' | 'cita_no_show';
  priority: 'critical' | 'important' | 'info';
  title: string;
  message: string;
  related_entity_type?: 'lead' | 'appointment' | 'invoice' | 'product' | 'cliente' | 'none' | null;
  related_entity_id?: string | null;
  metadata?: any;
  is_read: boolean;
  is_completed: boolean;
  is_snoozed: boolean;
  snooze_until?: string | null;
  completed_at?: string | null;
  completed_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AlertPreferences {
  id?: string;
  empresa_id: string;
  usuario_id?: string | null;
  enabled_alerts: Record<string, boolean>;
  factura_vencida_dias: number;
  lead_sin_contactar_horas: number;
  inventario_bajo_threshold: number;
  cita_confirmacion_horas: number;
  daily_digest_time: string;
  created_at?: string;
  updated_at?: string;
}

interface AlertsStore {
  alerts: Alert[];
  preferences: AlertPreferences | null;
  isLoading: boolean;
  unreadCount: number;
  error: string | null;
  
  // Alert management
  fetchAlerts: (empresaId: string, filters?: {
    priority?: string;
    isRead?: boolean;
  }) => Promise<void>;
  
  /** Quitar de la bandeja (elimina la fila) — misma acción interna que descartar / "Completar". */
  markAsRead: (alertId: string) => Promise<void>;
  markAsCompleted: (alertId: string, userId: string) => Promise<void>;
  snoozeAlert: (alertId: string, snoozeUntil: Date) => Promise<void>;
  dismissAlert: (alertId: string) => Promise<void>;
  
  // Bulk actions
  markAllAsRead: (empresaId: string, usuario?: { id: string; isAdmin: boolean }) => Promise<void>;
  /** La campana marcó todo leído: reflejarlo sin volver a consultar. */
  marcarLeidasLocal: (empresaId: string) => void;
  completeMultiple: (alertIds: string[], userId: string) => Promise<void>;
  
  // Preferences
  fetchPreferences: (empresaId: string, usuarioId?: string) => Promise<void>;
  updatePreferences: (preferences: Partial<AlertPreferences>) => Promise<void>;
  
  clearError: () => void;
}

export const useAlertsStore = create<AlertsStore>((set, get) => ({
  alerts: [],
  preferences: null,
  isLoading: false,
  unreadCount: 0,
  error: null,
  
  fetchAlerts: async (empresaId: string, filters = {}) => {
    set({ isLoading: true, error: null });
    try {
      // Ensure unpaid-delivered facturas digest exists (no cron calls this elsewhere)
      const { data: unpaidRows } = await supabase
        .from('facturas')
        .select('id, numero, total, monto_pendiente, estado')
        .eq('empresa_id', empresaId)
        .in('estado_entrega', ['ENTREGADO', 'ENTREGADO_PARCIAL'])
        .in('estado_pago', ['PENDIENTE_PAGO', 'PAGO_PARCIAL']);
      const unpaidFacturas = unpaidRows?.filter(
        (f: { estado?: string }) => f.estado !== 'ARCHIVADA' && f.estado !== 'ELIMINADA'
      ) ?? [];
      const count = unpaidFacturas.length;
      if (count > 0) {
        const totalAmount = unpaidFacturas?.reduce(
          (sum, f) => sum + (f.monto_pendiente ?? f.total ?? 0),
          0
        ) ?? 0;
        const { data: existing } = await supabase
          .from('alerts')
          .select('id')
          .eq('empresa_id', empresaId)
          .eq('type', 'factura_no_pagada')
          .eq('is_completed', false)
          .limit(1);
        if (!existing?.length) {
          const firstId = unpaidFacturas[0]?.id;
          const numeros = unpaidFacturas.map((f: { numero?: string }) => f.numero || '').filter(Boolean);
          await createUnpaidDeliveredDigestAlert(empresaId, count, totalAmount, 'COP', {
            firstFacturaId: firstId,
            numeros,
          });
        }
      }

      let query = supabase
        .from('alerts')
        .select('*')
        .eq('empresa_id', empresaId)
        // Pending = not completed (dismiss deletes the row; is_read is legacy)
        .eq('is_completed', false)
        .order('created_at', { ascending: false });
      
      if (filters.priority) {
        query = query.eq('priority', filters.priority);
      }
      if (filters.isRead !== undefined) {
        query = query.eq('is_read', filters.isRead);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      const now = Date.now();
      const rawAlerts = ((data as Alert[]) || []).filter((a) => {
        if (!a.is_snoozed || !a.snooze_until) return true;
        return new Date(a.snooze_until).getTime() <= now;
      });
      const alerts = collapseInventarioBajoAlerts(rawAlerts);
      const collapsedIds = new Set(alerts.map((a) => a.id));
      const inventarioDupes = rawAlerts.filter(
        (a) => a.type === 'inventario_bajo' && !collapsedIds.has(a.id)
      );
      if (inventarioDupes.length > 0) {
        void supabase
          .from('alerts')
          .update({ is_completed: true, is_read: true, updated_at: new Date().toISOString() })
          .in(
            'id',
            inventarioDupes.map((a) => a.id)
          );
      }
      const unread = alerts.filter(a => !a.is_read && !a.is_completed).length;
      
      set({ 
        alerts,
        unreadCount: unread,
        isLoading: false 
      });
    } catch (error: any) {
      console.error('Error fetching alerts:', error);
      set({ 
        error: error.message || 'Error al cargar alertas',
        isLoading: false 
      });
    }
  },
  
  markAsRead: async (alertId: string) => {
    // Antes solo ponía is_read: la alerta seguía en la lista. "Leída" = ya atendida: eliminar fila
    // (misma semántica que Completar / descartar).
    await get().dismissAlert(alertId);
  },
  
  markAsCompleted: async (alertId: string, _userId: string) => {
    try {
      const { error } = await supabase
        .from('alerts')
        .delete()
        .eq('id', alertId);
      
      if (error) throw error;
      
      set(state => {
        const alert = state.alerts.find(a => a.id === alertId);
        const wasUnread = !!(alert && !alert.is_read && !alert.is_completed);
        const updatedAlerts = state.alerts.filter(a => a.id !== alertId);
        const unread = wasUnread
          ? Math.max(0, state.unreadCount - 1)
          : updatedAlerts.filter(a => !a.is_read && !a.is_completed).length;
        
        return {
          alerts: updatedAlerts,
          unreadCount: unread
        };
      });
    } catch (error: any) {
      console.error('Error completing alert:', error);
      set({ error: error.message || 'Error al completar alerta' });
    }
  },
  
  snoozeAlert: async (alertId: string, snoozeUntil: Date) => {
    try {
      const { error } = await supabase
        .from('alerts')
        .update({ 
          is_snoozed: true,
          snooze_until: snoozeUntil.toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', alertId);
      
      if (error) throw error;
      
      set(state => ({
        alerts: state.alerts.map(a => 
          a.id === alertId 
            ? { ...a, is_snoozed: true, snooze_until: snoozeUntil.toISOString() }
            : a
        )
      }));
    } catch (error: any) {
      console.error('Error snoozing alert:', error);
      set({ error: error.message || 'Error al posponer alerta' });
    }
  },
  
  dismissAlert: async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('alerts')
        .delete()
        .eq('id', alertId);
      
      if (error) throw error;
      
      set(state => {
        const alert = state.alerts.find(a => a.id === alertId);
        const _wasUnread = alert && !alert.is_read && !alert.is_completed;
        
        const updatedAlerts = state.alerts.filter(a => a.id !== alertId);
        const unread = _wasUnread 
          ? Math.max(0, state.unreadCount - 1)
          : updatedAlerts.filter(a => !a.is_read && !a.is_completed).length;
        
        return {
          alerts: updatedAlerts,
          unreadCount: unread
        };
      });
    } catch (error: any) {
      console.error('Error dismissing alert:', error);
      set({ error: error.message || 'Error al descartar alerta' });
    }
  },
  
  markAllAsRead: async (empresaId: string, usuario?: { id: string; isAdmin: boolean }) => {
    try {
      const { error } = await supabase
        .from('alerts')
        .delete()
        .eq('empresa_id', empresaId);
      
      if (error) throw error;

      // Las notificaciones de la campana también quedan leídas.
      if (usuario?.id) {
        await marcarTodasNotificacionesLeidasWeb(empresaId, usuario.id, usuario.isAdmin);
      }
      
      set(state => ({
        alerts: state.alerts.filter((a) => a.empresa_id !== empresaId),
        unreadCount: state.alerts
          .filter((a) => a.empresa_id !== empresaId)
          .filter((a) => !a.is_read && !a.is_completed).length,
      }));
    } catch (error: any) {
      console.error('Error marking all as read:', error);
      set({ error: error.message || 'Error al quitar las alertas' });
    }
  },
  
  completeMultiple: async (alertIds: string[], _userId: string) => {
    try {
      if (alertIds.length === 0) return;
      const { error } = await supabase
        .from('alerts')
        .delete()
        .in('id', alertIds);
      
      if (error) throw error;
      
      set(state => {
        const removedUnread = state.alerts.filter(
          a => alertIds.includes(a.id) && !a.is_read && !a.is_completed
        ).length;
        const updatedAlerts = state.alerts.filter(a => !alertIds.includes(a.id));
        const unread = Math.max(0, state.unreadCount - removedUnread);
        
        return {
          alerts: updatedAlerts,
          unreadCount: unread
        };
      });
    } catch (error: any) {
      console.error('Error completing multiple:', error);
      set({ error: error.message || 'Error al completar alertas' });
    }
  },
  
  fetchPreferences: async (empresaId: string, usuarioId?: string) => {
    try {
      let query = supabase
        .from('alert_preferences')
        .select('*')
        .eq('empresa_id', empresaId);
      
      if (usuarioId) {
        query = query.eq('usuario_id', usuarioId);
      }
      
      const { data, error } = await query.maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      
      set({ preferences: data || null });
    } catch (error: any) {
      console.error('Error fetching preferences:', error);
      set({ error: error.message || 'Error al cargar preferencias' });
    }
  },
  
  updatePreferences: async (updates: Partial<AlertPreferences>) => {
    const { preferences } = get();
    if (!preferences && !updates.empresa_id) {
      console.error('Cannot update preferences: no existing preferences and no empresa_id provided');
      return;
    }
    
    try {
      const payload: any = {
        ...(preferences || {}),
        ...updates,
        updated_at: new Date().toISOString(),
      };
      
      // Ensure empresa_id is set
      if (!payload.empresa_id && preferences) {
        payload.empresa_id = preferences.empresa_id;
      }
      
      const { data, error } = await supabase
        .from('alert_preferences')
        .upsert(payload)
        .select()
        .single();
      
      if (error) throw error;
      
      set({ preferences: data as AlertPreferences });

      // Al apagar "Inventario bajo", la base cierra esas alertas: quitarlas ya de la lista.
      if ((data as AlertPreferences)?.enabled_alerts?.inventario_bajo === false) {
        set(state => {
          const alerts = state.alerts.filter(a => a.type !== 'inventario_bajo');
          return { alerts, unreadCount: alerts.filter(a => !a.is_read && !a.is_completed).length };
        });
      }
    } catch (error: any) {
      console.error('Error updating preferences:', error);
      set({ error: error.message || 'Error al guardar preferencias' });
    }
  },
  
  marcarLeidasLocal: (empresaId: string) => {
    set(state => ({
      alerts: state.alerts.map(a => (a.empresa_id === empresaId ? { ...a, is_read: true } : a)),
      unreadCount: 0,
    }));
  },

  clearError: () => set({ error: null }),
}));
